/**
 * Section ground → consistent surface/text/border classes, so a dark section
 * never ends up with dark text. Ported from the POC's lib/theme.ts.
 */
export type Theme = "paper" | "mist" | "ink" | "accent";

export interface ThemeClasses {
  surface: string;
  card: string;
  heading: string;
  body: string;
  muted: string;
  border: string;
  prose: string;
  accentText: string;
  accentTextHover: string;
  accentTextGroupHover: string;
  isDark: boolean;
}

const PAPER: ThemeClasses = {
  surface: "bg-paper",
  card: "bg-paper-raised border border-hairline",
  heading: "text-ink",
  body: "text-slate",
  muted: "text-slate",
  border: "border-hairline",
  prose: "prose-cw",
  accentText: "text-accent-ink",
  accentTextHover: "hover:text-accent-ink",
  accentTextGroupHover: "group-hover:text-accent-ink",
  isDark: false,
};

const MIST: ThemeClasses = { ...PAPER, surface: "bg-mist" };

const INK: ThemeClasses = {
  surface: "bg-ink",
  card: "bg-ink-2 border border-white/10",
  heading: "text-paper-raised",
  body: "text-white/75",
  muted: "text-white/60",
  border: "border-white/10",
  prose: "prose-cw prose-cw-invert",
  accentText: "text-signal",
  accentTextHover: "hover:text-signal",
  accentTextGroupHover: "group-hover:text-signal",
  isDark: true,
};

const ACCENT: ThemeClasses = {
  ...INK,
  surface: "bg-accent",
  card: "bg-white/10 border border-white/15",
  body: "text-white/80",
  muted: "text-white/65",
  border: "border-white/15",
};

const THEMES: Record<Theme, ThemeClasses> = { paper: PAPER, mist: MIST, ink: INK, accent: ACCENT };

export function themeClasses(theme: Theme | undefined): ThemeClasses {
  return THEMES[theme ?? "paper"] ?? PAPER;
}

export function eyebrowClass(theme: Theme | undefined): string {
  const t = theme ?? "paper";
  return t === "ink" || t === "accent" ? "text-signal" : "text-signal-ink";
}
